import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clock, CheckCircle, XCircle, AlertCircle, ExternalLink } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { getOrganizationLogs } from '../../../services/api';

const PromptLogsBlock = ({ user, size = 'medium', refreshTrigger }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLogs();
  }, [user, refreshTrigger]);

  const loadLogs = async () => {
    if (!user?.organization_id) return;
    
    try {
      const logsData = await getOrganizationLogs(user.organization_id, { 
        limit: 10,
        sort: 'created_at',
        order: 'desc'
      });
      setLogs(logsData.logs || []);
    } catch (error) {
      console.error('Failed to load logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDisplayCount = () => {
    switch (size) {
      case 'small':
        return 6;
      case 'medium':
        return 8;
      case 'large':
        return 8;
      default:
        return 4;
    }
  };

  const displayLogs = logs.slice(0, getDisplayCount());

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return CheckCircle;
      case 'error':
        return XCircle;
      case 'warning':
        return AlertCircle;
      default:
        return Clock;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'text-celadon-600 dark:text-celadon-400';
      case 'error':
        return 'text-red-600 dark:text-red-400';
      case 'warning':
        return 'text-orange-500 dark:text-orange-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center">
        <Clock className="h-8 w-8 text-gray-400 dark:text-gray-500 mb-2" />
        <p className="text-sm text-gray-600 dark:text-gray-400">No recent logs</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-hidden">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-gray-600 dark:text-gray-400" />
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Recent Logs</span>
        </div>
        <Link 
          to="/logs"
          className="text-xs text-celadon-600 hover:text-celadon-700 dark:text-celadon-400 dark:hover:text-celadon-300"
        >
          View all
        </Link>
      </div>

      <div className="space-y-2 overflow-y-auto max-h-full">
        {displayLogs.map((log) => {
          const StatusIcon = getStatusIcon(log.status);
          
          return (
            <div key={log.id} className="p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <StatusIcon className={`h-3 w-3 ${getStatusColor(log.status)}`} />
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {log.prompt_name || 'Unknown Prompt'}
                    </span>
                  </div>
                  
                  {size !== 'small' && (
                    <div className="flex items-center gap-3 text-xs text-gray-600 dark:text-gray-400">
                      {log.execution_time && (
                        <span>{log.execution_time}s</span>
                      )}
                      {log.model && (
                        <span>{log.model}</span>
                      )}
                      {log.total_tokens && (
                        <span>{log.total_tokens} tokens</span>
                      )}
                    </div>
                  )}
                  
                  <div className="flex items-center gap-1 mt-1">
                    <Clock className="h-3 w-3 text-gray-400 dark:text-gray-500" />
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {log.created_at && !isNaN(new Date(log.created_at).getTime()) 
                        ? formatDistanceToNow(new Date(log.created_at), { addSuffix: true })
                        : 'Just now'
                      }
                    </span>
                  </div>
                </div>
                
                <Link
                  to={`/logs`}
                  className="p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 flex-shrink-0"
                  title="View log details"
                >
                  <ExternalLink className="h-3 w-3" />
                </Link>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PromptLogsBlock; 